import { ImageFragment } from '@lib/umbraco/generated/types.generated'
import clsx from 'clsx'
import Head from 'next/head'
import { CSSProperties, forwardRef } from 'react'
import s from './Media.module.scss'
const deviceSizes = [128, 256, 384, 640, 750, 828, 1080, 1200, 1440, 1920]
interface ImageProps {
  inView: boolean
  image: ImageFragment
  sizes?: string
  priority?: boolean
  className?: string
}

const Image = forwardRef<HTMLImageElement, ImageProps>(
  ({ className, image, sizes, priority, inView }, ref) => {
    //   const error = useRef<boolean>(false)
    //   const [error, setError] = useState<boolean>(false)

    const loader = ({ image, width }: ImageLoaderProps): string => {
      if (!image.url) return ''

      if (image.url.includes('{size}'))
        return image.url.replace('{size}', width.toString())
      if (!image.id && image.url) return image.url
      return `https://media.gubi.com/${image.id}-${width}.webp`
    }
    const loadImage = priority === true || inView
    const hasMobileImage = image?.mobileImage?.media.__typename === 'Image'
    const getFocalPoint = (image: Partial<ImageFragment>): string =>
      image?.umbracoFile?.focalPoint
        ? `${image.umbracoFile.focalPoint.left * 100}% ${
            image.umbracoFile.focalPoint.top * 100
          }%`
        : '50% 50%'

    let imgAttributes: GenImgAttrsResult = {
      src: undefined,
      srcSet: undefined,
      sizes: undefined,
    }
    let mobileImageAttributes: GenImgAttrsResult = {
      src: undefined,
      srcSet: undefined,
      sizes: undefined,
    }
    let styleAttributes = {
      style: undefined,
    }
    if (loadImage) {
      imgAttributes = generateImgAttrs({
        image,
        sizes,
        loader,
      })
      styleAttributes = {
        style: {
          '--focal': getFocalPoint(image),
        } as CSSProperties,
      }

      if (hasMobileImage) {
        mobileImageAttributes = generateImgAttrs({
          image: image.mobileImage.media as ImageFragment,
          sizes,
          loader,
        })
        styleAttributes = {
          style: {
            ...styleAttributes.style,
            '--m-focal': getFocalPoint(
              image.mobileImage.media as ImageFragment,
            ),
          } as CSSProperties,
        }
      }
    }

    return (
      <>
        <picture>
          {hasMobileImage && (
            <source
              media="(max-width: 834px)"
              {...{ ...mobileImageAttributes, src: undefined }}
            />
          )}
          <source
            media="(min-width: 0px)"
            {...{ ...imgAttributes, src: undefined }}
          />
          <img
            id={image.id}
            src={imgAttributes.src}
            alt={image.alt}
            className={clsx(className, { [s.priority]: priority })}
            onLoad={(event) => {
              if (event.currentTarget.complete) {
                event.currentTarget.classList.add(s.loaded)
              }
            }}
            ref={ref}
            {...styleAttributes}
          />
        </picture>
        {priority ? (
          // Note how we omit the `href` attribute, as it would only be relevant
          // for browsers that do not support `imagesrcset`, and in those cases
          // it would likely cause the incorrect image to be preloaded.
          //
          // https://html.spec.whatwg.org/multipage/semantics.html#attr-link-imagesrcset
          <Head>
            <link
              key={
                '__nimg-' +
                imgAttributes.src +
                imgAttributes.srcSet +
                imgAttributes.sizes
              }
              rel="preload"
              as="image"
              href={imgAttributes.srcSet ? undefined : imgAttributes.src}
              imageSrcSet={imgAttributes.srcSet}
              //@ts-ignore
              imageSizes={imgAttributes.sizes}
            />
          </Head>
        ) : null}
      </>
    )
  },
)

Image.displayName = 'Image'
export default Image

type ImageLoaderProps = {
  image: ImageFragment
  width: number
}

type ImageLoader = (props: ImageLoaderProps) => string

type GenImgAttrsData = {
  image: ImageFragment
  loader: ImageLoader
  width?: number
  sizes?: string
}

type GenImgAttrsResult = {
  src: string
  srcSet: string | undefined
  sizes: string | undefined
}

function generateImgAttrs({
  image,
  width,
  sizes,
  loader,
}: GenImgAttrsData): GenImgAttrsResult {
  const { widths, kind } = getWidths(width, sizes)
  const last = widths.length - 1
  return {
    sizes: !sizes && kind === 'w' ? '100vw' : sizes,
    srcSet: widths
      .map(
        (w, i) =>
          `${loader({ image, width: w })} ${kind === 'w' ? w : i + 1}${kind}`,
      )
      .join(', '),

    // It's intended to keep `src` the last attribute because React updates
    // attributes in order. If we keep `src` the first one, Safari will
    // immediately start to fetch `src`, before `sizes` and `srcSet` are even
    // updated by React. That causes multiple unnecessary requests if `srcSet`
    // and `sizes` are defined.
    // This bug cannot be reproduced in Chrome or Firefox.
    src: loader({ image, width: widths[last] }),
  }
}
function getWidths(
  width: number | undefined,
  sizes: string | undefined,
): { widths: number[]; kind: 'w' | 'x' } {
  if (sizes) {
    // Find all the "vw" percent sizes used in the sizes prop
    const viewportWidthRe = /(^|\s)(1?\d?\d)vw/g
    const percentSizes = []
    for (let match; (match = viewportWidthRe.exec(sizes)); match) {
      percentSizes.push(parseInt(match[2]))
    }
    if (percentSizes.length) {
      const smallestRatio = Math.min(...percentSizes) * 0.01
      return {
        widths: deviceSizes.filter((s) => s >= deviceSizes[0] * smallestRatio),
        kind: 'w',
      }
    }
    return { widths: deviceSizes, kind: 'w' }
  }
  if (typeof width !== 'number') {
    return { widths: deviceSizes, kind: 'w' }
  }

  const widths = [
    //@ts-ignore
    ...new Set(
      // > This means that most OLED screens that say they are 3x resolution,
      // > are actually 3x in the green color, but only 1.5x in the red and
      // > blue colors. Showing a 3x resolution image in the app vs a 2x
      // > resolution image will be visually the same, though the 3x image
      // > takes significantly more data. Even true 3x resolution screens are
      // > wasteful as the human eye cannot see that level of detail without
      // > something like a magnifying glass.
      // https://blog.twitter.com/engineering/en_us/topics/infrastructure/2019/capping-image-fidelity-on-ultra-high-resolution-devices.html
      [width, width * 2 /*, width * 3*/].map(
        (w) =>
          deviceSizes.find((p) => p >= w) ||
          deviceSizes[deviceSizes.length - 1],
      ),
    ),
  ]
  return { widths, kind: 'x' }
}
